import urls from '@/legacy/config/env';
import { REFETCH_DELAY } from '@/legacy/constants';
import { useToast } from '@/legacy/hooks';
import { notificationsTradPrefix } from '@/legacy/i18n';
import { fetchRequest } from '@/legacy/services';
import { useStore } from '@/legacy/store/zustand';
import { TaskStatus } from '@/legacy/types';
import { TaskResponse } from '@/legacy/types/taskTypes';
import { useQuery } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const SingleTaskWatcher = ({ taskId }: { taskId: string }) => {
	const setTaskStatus = useStore((s) => s.setTaskStatus);
	const clearTask = useStore((s) => s.clearTask);
	const addNotification = useStore((s) => s.addNotification);
	const task = useStore((s) => s.tasks[taskId]);
	const { showToast } = useToast();
	const { t } = useTranslation();

	const query = useQuery({
		queryKey: ['task-status', taskId],
		queryFn: () =>
			fetchRequest<TaskResponse>({
				url: `${urls.tasks.taskStatus}?task_id=${taskId}`,
				method: 'GET',
			}),
		enabled: !!taskId,
		refetchInterval: (query) => {
			const status = query?.state.data?.status;

			if (typeof status === 'string' && (status === 'done' || status === 'failed')) return false;

			return REFETCH_DELAY;
		},
	});

	const prevStatusRef = useRef<string>(undefined);

	useEffect(() => {
		const status = query.data?.data?.status as TaskStatus;
		if (status && (status === 'done' || status === 'failed') && prevStatusRef.current !== status) {
			setTaskStatus(taskId, status);
			clearTask(taskId);
			addNotification({
				id: nanoid(),
				created_at: new Date().toISOString(),
				readed: false,
				source: 'frontend',
				type: status === 'done' ? 'success' : 'error',
				title: `${task?.label} ${status === 'done' ? t(`${notificationsTradPrefix}.completed`) : t(`${notificationsTradPrefix}.failed`)}`,

				body:
					status === 'done'
						? (task?.successNotificationMessage as string)
						: (task?.errorNotificationMessage as string),
				url: task?.url,
			});
			showToast(
				status === 'done'
					? `${task?.label ?? 'Task'} ${t(`${notificationsTradPrefix}.completed`)}.`
					: `${task?.label ?? 'Task'} ${t(`${notificationsTradPrefix}.failed`)}.`,
				status === 'done' ? 'success' : 'error',
			);

			prevStatusRef.current = status;
		}
	}, [query.data, taskId]);

	return null;
};

const TaskTracker = () => {
	const tasks = useStore((s) => s.tasks);
	return (
		<>
			{Object.values(tasks).map((task) => (
				<SingleTaskWatcher
					key={task.taskId}
					taskId={task.taskId}
				/>
			))}
		</>
	);
};

export default TaskTracker;
